import Basket from 'icons/lib/basket.svg';
import Link from 'next/link';
import { useRef, useState } from 'react';
import { Button, useWindowDimensions } from 'ui';

import {
  addToCartEvent,
  clickProductListPageEvent,
} from '@/modules/google/data-layer/data-layer';
import { useProductList } from '@/modules/product/context/productList';
import { Image } from '@/shared/components/image';
import { useBasket } from '@/shared/context/basket-context';
import { productName } from '@/shared/utils/product-name';

import styles from './ProductSlider.module.scss';
import { ProductSliderProps } from './types';
import { imageBreakpoints } from './utils';

export const ProductSlider = ({
  href,
  images,
  index,
  listId,
  listName,
  product,
  onAddToCartClick,
  onClick,
  attributionToken,
}: ProductSliderProps) => {
  const hovered = useRef(false);
  const { addToBasket, setIsCartSidebarOpen } = useBasket();
  const { page, limit } = useProductList();
  const slidesWrapperRef = useRef<HTMLDivElement>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const { width } = useWindowDimensions();

  const handlePrevClick = () => {
    setCurrentSlide((oldValue) => {
      if (!slidesWrapperRef.current) {
        return oldValue;
      }

      const newValue = oldValue - 1;

      slidesWrapperRef.current.style.transform =
        newValue === 0 ? 'translateX(0%)' : `translateX(-${newValue}00%)`;

      return newValue;
    });
  };

  const handleNextClick = () => {
    setCurrentSlide((oldValue) => {
      if (!slidesWrapperRef.current) {
        return oldValue;
      }

      const newValue = oldValue + 1;

      slidesWrapperRef.current.style.transform = `translateX(-${newValue}00%)`;

      return newValue;
    });
  };

  const handleMouseEnter = () => {
    if (
      hovered.current ||
      width < 768 ||
      images.length < 2 ||
      currentSlide === images.length - 1
    ) {
      return;
    }

    hovered.current = true;
    handleNextClick();
  };

  const handleMouseLeave = () => {
    if (images.length < 2 || !slidesWrapperRef.current) {
      return;
    }

    hovered.current = false;
    slidesWrapperRef.current.style.transform = 'translateX(0%)';
    setCurrentSlide(0);
  };

  const handleProductClick = () => {
    onClick?.(product);

    clickProductListPageEvent(
      product,
      listId,
      listName,
      product.currency,
      index,
      { page, limit }
    );
  };

  const handleAddToCart = () => {
    onAddToCartClick?.(product);
    setIsCartSidebarOpen(true);
    addToBasket(product.productId);
    addToCartEvent([
      {
        id: product.productId,
        name: productName(product),
        currency: product.currency,
        brand: product.brandName,
        categoryPath: product.categoryHomePath,
        price: product.price,
        quantity: 1,
        importFlag: product.importFlag,
        attributionToken: attributionToken,
      },
    ]);
  };

  return (
    <div
      className={styles.productSlider__container}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className={styles.productSlider__slidesWrapper}
        ref={slidesWrapperRef}
      >
        {images.map((image, slideIndex) => (
          <div className={styles.productSlider__slide} key={slideIndex}>
            <Link prefetch={false} href={href} onClick={handleProductClick}>
              <Image
                alt={productName(product)}
                title={productName(product)}
                breakpoints={imageBreakpoints}
                src={image}
                loading={index === 0 && slideIndex === 0 ? 'eager' : 'lazy'}
                fetchPriority={index <= 3 ? 'high' : 'low'}
              />
            </Link>
          </div>
        ))}
      </div>

      {images.length > 1 ? (
        <>
          <Button
            buttonName="arrow arrowPrev"
            isDisabled={currentSlide === 0}
            onPress={handlePrevClick}
          />
          <Button
            buttonName="arrow arrowNext"
            isDisabled={currentSlide === images.length - 1}
            onPress={handleNextClick}
          />
        </>
      ) : null}

      {!!product.stock && (
        <Button
          buttonName="cartButton"
          variant="iconButton"
          size="icon"
          onPress={handleAddToCart}
        >
          <Basket />
        </Button>
      )}
    </div>
  );
};
